import { useCallback, useState } from 'react';
import { api } from '../api/client';

/**
 * Epic 49 walkthrough feedback item 1 + 3. Calibration judging is blind on both
 * axes — score-blind and identity-blind — because the reveal screen measures
 * agreement between the recruiter's verdict and Arbie's score, and refinement
 * learns from the disagreement. That is only sound if both judged the same
 * artifact: the model reads the sanitized resume, so the recruiter must too.
 *
 * The SmartRecruiters link is not a judging *input*, it is the next action
 * *after* judging — so it lives behind one deliberate click, and the server
 * records `identity_revealed_at` when this fires.
 *
 * Deliberately NOT a React Query hook: this must fire only on an explicit
 * click (a query would fetch on mount and defeat the whole point), and the
 * revealed state must not be cached across candidates.
 */
/**
 * NOT re-declared here. This is the mirrored copy of the type the identity
 * route annotates its reply with (`packages/web/src/types/calibration-api.ts`
 * ↔ `packages/shared/src/types/calibration-api.ts`, byte-for-byte drift
 * guarded). The local interface this replaces was a strict subset of the
 * server's response — it compiled, but it is the same "client re-declares the
 * contract" pattern that let this page ship against an API that did not exist.
 * In particular it omitted `srProfileUrl`, which is why the card was rebuilding
 * the SmartRecruiters URL from a fragment instead of rendering the one the
 * server already built.
 */
export type { CalibrationIdentityResponse as CalibrationIdentity } from '../types/calibration-api';
import type { CalibrationIdentityResponse } from '../types/calibration-api';

export function useCalibrationIdentity(agentId: number, rowId: number) {
  const [identity, setIdentity] = useState<CalibrationIdentityResponse | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reveal = useCallback(async () => {
    if (identity || isRevealing) return;
    setIsRevealing(true);
    setError(null);
    try {
      const result = await api.get<CalibrationIdentityResponse>(
        `/api/sourcing-agents/${agentId}/calibration-candidates/${rowId}/identity`
      );
      setIdentity(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this candidate’s identity.');
    } finally {
      setIsRevealing(false);
    }
  }, [agentId, rowId, identity, isRevealing]);

  return { identity, isRevealing, error, reveal };
}
